import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './App.css';
import NavSection from './components/NavSection';
import Footer from './components/Footer';
import bg from './bg.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTurnDown, faArrowTurnUp } from '@fortawesome/free-solid-svg-icons';

function App() {
  const [businessDetails, setBusinessDetails] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [reviews, setReviews] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [map, setMap] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const autocompleteRef = useRef(null);

  const toggleReadMore = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  const initializeAutocomplete = useCallback(() => {
    if (!window.google || !autocompleteRef.current) return;
  
    const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, {
      types: ['establishment'],
    });
  
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.name) {
        setBusinessName(place.name);
      }
    });
  }, []);

  
  const initMap = useCallback(() => {
    if (!businessDetails || !businessDetails.placeId || map || !window.google) {
      return;
    }
  
    const mapOptions = { zoom: 15 };
    const mapContainer = document.getElementById('map');
    const mapInstance = new window.google.maps.Map(mapContainer, mapOptions);
  
    const service = new window.google.maps.places.PlacesService(mapInstance);
    const request = {
      placeId: businessDetails.placeId,
      fields: ['geometry', 'name', 'formatted_address', 'international_phone_number', 'website', 'business_status', 'opening_hours'],
    };
  
    service.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        if (place.geometry && place.geometry.location) {
          mapInstance.setCenter(place.geometry.location);
          mapInstance.setZoom(15);
  
          new window.google.maps.Marker({
            map: mapInstance,
            position: place.geometry.location,
            title: place.name,
          });
  
          setMap(mapInstance);
        }
      } else {
        console.error('Error loading map details:', status);
      }
    });
  }, [businessDetails, map]); 
  
  
  const loadGoogleMapsScript = useCallback(async () => {
    try {
      const response = await fetch('https://api-rggjpj4zxq-uc.a.run.app/get-google-maps-script');
      if (!response.ok) {
        console.error('Failed to fetch Google Maps API script URL:', response.statusText);
        return;
      }
  
      const data = await response.json();
      const scriptUrl = data.scriptUrl;
  
      if (document.querySelector(`script[src="${scriptUrl}"]`)) {
        console.log('Google Maps API script already loaded.');
        initializeAutocomplete();
        initMap();
        return;
      }
  
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
  
      script.onload = () => {
        console.log('Google Maps API loaded successfully');
        initializeAutocomplete();
        initMap();
      };
  
      script.onerror = (error) => {
        console.error('Error loading Google Maps API:', error);
      };
  
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error fetching Google Maps API URL:', error);
    }
  }, [initializeAutocomplete, initMap]);
  
  useEffect(() => {
    if (!window.google) {
      loadGoogleMapsScript();
    } else {
      initializeAutocomplete();
    }
  }, [loadGoogleMapsScript, initializeAutocomplete]);

  useEffect(() => {
    if (businessDetails && businessDetails.placeId) {
      initMap();
    }
  }, [businessDetails, initMap]);
  
  const handleFetchDetails = async () => {
    if (!businessName) {
      alert('Please enter a business name');
      return;
    }

    try {
      const response = await fetch(`https://api-rggjpj4zxq-uc.a.run.app/get-business-details?name=${businessName}`);
      if (response.ok) {
        const data = await response.json();
        setBusinessDetails(data);
      } else {
        console.error('Failed to fetch:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
    }
  };

  const handleShowReviews = async () => {
    if (!businessDetails || !businessDetails.placeId) return;

    try {
      const response = await fetch(`https://api-rggjpj4zxq-uc.a.run.app/get-business-reviews?placeid=${businessDetails.placeId}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews.slice(0, 5));
        setShowReviews(true);
      } else {
        console.error('Failed to fetch reviews:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleHideReviews = () => {
    setShowReviews(false);
  };

  const handleMoreInfo = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleViewOnGoogleMap = () => {
    window.open(`https://www.google.com/maps/search/?q=${businessDetails.businessLink}`, '_blank');
  };

  const handleSearchAgain = () => {
    window.location.reload();
  };

  return (
  <div> 
    <Helmet>
        <title>Review Link Generator</title>
      </Helmet>
    <div>
      <NavSection />
      <div
      className="container"
      style={{
        backgroundImage: `url(${bg})`,
      }}
      >
      <h1 className="heading">
        Google Business Profile <span className="highlight">Review Link</span> Generator
      </h1>
      <center><p className="description">
        With a single click, now you can generate a Google review link for your business and share it with your customers.
      </p></center>
      
          <div className="input-group" style={{ display: 'flex', justifyContent: 'center' }}>
              <input
                type="text"
                ref={autocompleteRef}
                placeholder="Enter your business name"
                className="input-field"
                style={{
                  padding: '0.5rem',
                  fontSize: '1rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '800px', 
                  marginRight: '10px',
                  fontFamily: 'Montserrat, Helvetica, Arial, Lucida, sans-serif',
                }}
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
              <button
                className="fetch-button"
                onClick={handleFetchDetails}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '1rem',
                  backgroundColor: '#65af44',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontFamily: 'Montserrat, Helvetica, Arial, Lucida, sans-serif',
                }}
              >
                Fetch Details
              </button>
            </div>
         
            {businessDetails && (
  <div className="cards-container">
    <div className="card">
      <h3>Share your Google Reviews</h3>
      <p>Share the link with your customers to show your Google Listing reviews.</p>
      <a
        href={businessDetails.reviewLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        [{businessDetails.reviewLink.slice(0, 11)}...] Click here
      </a>
      <br />
      <br />
      {!showReviews ? (
        <button onClick={handleShowReviews}>
          Show Reviews <FontAwesomeIcon icon={faArrowTurnDown} />
        </button>
      ) : (
        <button onClick={handleHideReviews}>
          Hide Reviews <FontAwesomeIcon icon={faArrowTurnUp} />
        </button>
      )}
    </div>

    <div className="card">
      <h3>Get Reviews</h3>
      <p>Share this link so that customers can write a review without additional effort.</p>
      <a
        href={businessDetails.writeReviewLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        [{businessDetails.writeReviewLink.slice(0, 11)}...] Click here
      </a>
      <button
        onClick={() => {
          navigator.clipboard.writeText(businessDetails.writeReviewLink);
        }}
      >
        Copy & Share
      </button>
    </div>

    <div className="card">
      <h3>View your Google Listing in SERP</h3>
      <p>Click the link and see how your business and associated information appear in search result.</p>
      <a
        href={businessDetails.serplink}
        target="_blank"
        rel="noopener noreferrer"
      >
        [{businessDetails.serplink.slice(0, 11)}...] Click here
      </a>
      <button
        onClick={() => {
          window.open(businessDetails.serplink, '_blank');
        }}
      >
        View In SERP
      </button>
    </div>
  </div>
)}

{showReviews && (
  <div className="reviews-container">
    {reviews.map((review, index) => (
      <div key={index} className="review-card">
        <div className="review-content">
          <p><strong>{review.authorName}</strong></p>
          <p><em>{review.date}</em></p>
          <p>{review.text}</p>
        </div>
      </div>
    ))}
  </div>
)}
      {businessDetails && (
        <div className="map-container">
          <div id="map"></div>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button onClick={handleMoreInfo}>More Information</button>
            <button onClick={handleViewOnGoogleMap}>View On Google Map</button>
            <button onClick={handleSearchAgain}>Search Again</button>
          </div>
        </div>
      )}

      {showModal && businessDetails && (
        <div className="modal">
          <div className="modal-content">
            <h2>Business Information</h2>
            <p><strong>Place ID:</strong> {businessDetails.placeId}</p>
            <p><strong>Ludocid:</strong> {businessDetails.cid}</p>
            <p><strong>Address:</strong> {businessDetails.address}</p>
            <p><strong>Phone Number:</strong> {businessDetails.phone}</p>
            <p><strong>Website:</strong> {businessDetails.website}</p>
            <p><strong>Business Status:</strong> {businessDetails.status}</p>
            <p><strong>Business Hours:</strong> {businessDetails.hours}</p>
            <p><strong>Current Opening Status:</strong> {businessDetails.openingStatus}</p>
            <button onClick={handleCloseModal}>Close</button>
          </div>
        </div>
      )}

      <div className="faq-container">
        <h2 className="faq-heading">FAQ</h2>
        <div className="faq-cards-container">
          <div className="faq-card">
            <h3>What is Ludocid?</h3>
            <p>
              {expandedCard === 1 ? (
                <>
                  Ludocid or CID (customer ID number) is a distinct individual ID that Google allocates to a business location making it convenient for Google to ascertain the identity of the business across multiple Google platforms. This helps Google manage the business and its presence across its platforms such as Google Maps, Google Search, and other Google services.
                </>
              ) : (
                "Ludocid or CID (customer ID number) is a distinct individual ID that Google allocates to a business location making it convenient for Google to ascertain the identity of the business across multiple Google platforms."
              )}
            </p>
            <button className="read-more-btn" onClick={() => toggleReadMore(1)}>
              {expandedCard === 1 ? "Show Less" : "Read More"}
            </button>
          </div>
          <div className="faq-card">
            <h3>How Can Ludocid Be Used?</h3>
            <p>
              {expandedCard === 2 ? (
                <>
                  The Ludocid serves various purposes from helping identify business on Google search results & Google Maps to analyzing the performance of the business listing across various Google products. In addition to that, a ‘CID’ can be used to manage listings, support & tackle issues with a business profile, and link multiple Google profiles, improving business management on Google platforms.
                </>
              ) : (
                "The Ludocid serves various purposes from helping identify business on Google search results & Google Maps to analyzing the performance of the business listing across various Google products."
              )}
            </p>
            <button className="read-more-btn" onClick={() => toggleReadMore(2)}>
              {expandedCard === 2 ? "Show Less" : "Read More"}
            </button>
          </div>
          <div className="faq-card">
            <h3>What is Place ID?</h3>
            <p>
              {expandedCard === 3 ? (
                <>
                  The place ID, as the name suggests, is a standalone textual identifier associated with a place like business, landmark, or intersection that is referred to by Google to identify the specific business within its platforms. It helps in the accurate identification and mapping of the business location across various Google services, allowing for better search results and business visibility.
                </>
              ) : (
                "The place ID as the name suggests is a standalone textual identifier associated with a place like business, landmark, or intersection that is referred to by Google to identify the specific business within its platforms."
              )}
            </p>
            <button className="read-more-btn" onClick={() => toggleReadMore(3)}>
              {expandedCard === 3 ? "Show Less" : "Read More"}
            </button>
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
    </div>
  );
}

export default App;
